import { Col, Row, Collapse } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';
import statusDictionary from '../../../../helpers/statusDictionary.json';
import GreenCheckCircle from '../../../../images/GreenCheckCircle.svg';
import countrySettings from '../../../../pages/ExamPage/countrySettings.json';

const FALLBACK_SPECIALTY = 'Otros';

const SPECIALTY_STATE_CLASS = {
  [statusDictionary.done.status]: 'exam-step-finished',
  [statusDictionary.occupied.status]: 'exam-step-in-progress',
  [statusDictionary.wait.status]: 'exam-step-waiting',
};

const EXAM_STATE_CLASS = {
  [statusDictionary.done.status]: 'exam-sub-step-finished',
  [statusDictionary.inProgress.status]: 'exam-sub-step-finished',
  [statusDictionary.occupied.status]: 'exam-sub-step-in-progress',
  [statusDictionary.wait.status]: 'exam-sub-step-waiting',
};

const isExamFinished = (exam) =>
  exam.state === statusDictionary.done.status ||
  exam.state === statusDictionary.inProgress.status;

const resolveSpecialtyState = (exams) => {
  const allFinished = exams.every(isExamFinished);
  if (allFinished) return statusDictionary.done.status;

  const anyOccupied = exams.some((e) => e.state === statusDictionary.occupied.status);
  if (anyOccupied) return statusDictionary.occupied.status;

  const anyFinished = exams.some(isExamFinished);
  if (anyFinished) return statusDictionary.occupied.status;

  return statusDictionary.wait.status;
};

const groupExamsBySpecialty = (exams) => {
  const grouped = {};

  exams.forEach((exam) => {
    const key = exam.specialty_name || FALLBACK_SPECIALTY;
    if (!grouped[key]) {
      grouped[key] = { id: key, name: key, exams: [] };
    }
    grouped[key].exams.push(exam);
  });

  return Object.values(grouped)
    .map((group) => ({
      ...group,
      state: resolveSpecialtyState(group.exams),
    }))
    .sort((a, b) => {
      if (a.name === FALLBACK_SPECIALTY) return 1;
      if (b.name === FALLBACK_SPECIALTY) return -1;
      return 0;
    });
};

const getExamStepClass = (exam) => {
  if (exam.state === statusDictionary.created.status || exam.state === statusDictionary.wait.status) {
    return 'exam-step-waiting';
  }
  if (exam.state === statusDictionary.occupied.status || exam.state === statusDictionary.inProgress.status) {
    return 'exam-step-in-progress';
  }
  if (exam.state === statusDictionary.done.status) {
    return 'exam-step-finished';
  }
  return 'exam-step-waiting';
};

const ExamLegend = () => (
  <Row justify="center" className="border-container">
    <Col span={24}>
      <p>A medida que avances, el semáforo irá cambiando de color de la siguiente forma:</p>
    </Col>
    <Col span={24}>
      <div className="exam-steps-container">
        <div className="exam-step exam-step-waiting">
          <span className="exam-step-color-bar" />
          <span className="exam-step-title"><strong>En espera</strong></span>
        </div>
        <div className="exam-step exam-step-in-progress">
          <span className="exam-step-color-bar" />
          <span className="exam-step-title"><strong>En curso</strong></span>
        </div>
        <div className="exam-step exam-step-finished">
          <span className="exam-step-color-bar" />
          <span className="exam-step-title"><strong>Terminado</strong></span>
        </div>
      </div>
    </Col>
  </Row>
);

const ExamSubItem = ({ exam }) => {
  const stateClass = EXAM_STATE_CLASS[exam.state] ?? EXAM_STATE_CLASS[statusDictionary.wait.status];
  return (
    <div className={`exam-sub-step ${stateClass}`}>
      <span className="exam-step-title"><strong>{exam.name}</strong></span>
    </div>
  );
};

const ExamList = ({ exams }) => (
  <div className="exam-steps-container">
    {exams.map((exam) => {
      if (!exam.id) return null;
      const stateClass = getExamStepClass(exam);
      return (
        <div key={exam.id} className={`exam-step ${stateClass}`}>
          <span className="exam-step-color-bar" />
          <span className="exam-step-title"><strong>{exam.name}</strong></span>
        </div>
      );
    })}
  </div>
);

const SpecialtyList = ({ specialties }) => (
  <div className="exam-steps-container">
    <Collapse
      ghost
      className="patient-journey-collapse"
      expandIconPosition="end"
      expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
    >
      {specialties.map((specialty) => {
        if (!specialty.id) return null;
        const stateClass = SPECIALTY_STATE_CLASS[specialty.state] ?? SPECIALTY_STATE_CLASS[statusDictionary.wait.status];
        return (
          <Collapse.Panel
            key={specialty.id}
            header={<span className="exam-step-title"><strong>{specialty.name}</strong></span>}
            className={`exam-step-panel ${stateClass}`}
          >
            {specialty.exams.map((subExam) => (
              <ExamSubItem key={subExam.id} exam={subExam} />
            ))}
          </Collapse.Panel>
        );
      })}
    </Collapse>
  </div>
);

const ExamsStatus = ({ userData, status, countryCode }) => {
  const isSpecialtyEntity = countrySettings.useExamSpecialtyEntity?.[countryCode?.toUpperCase()] ?? false;
  const isAllDone = status === statusDictionary.done.status;

  const examsToDisplay = useMemo(() => {
    if (!userData?.exams) return [];
    if (isSpecialtyEntity) return groupExamsBySpecialty(userData.exams);
    return userData.exams;
  }, [userData?.exams, isSpecialtyEntity]);

  return (
    <Row justify="center">
      <Col span={24} className="text-center">
        {isAllDone ? <img alt="check" src={GreenCheckCircle} width={50} /> : <ExamLegend />}
      </Col>

      <Col span={24}>
        <Row justify="center">
          {isAllDone && (
            <Col span={16} className="mt-20">
              <h3 className="text-center text-green">
                <strong>¡Haz finalizado la toma de tus exámenes!</strong>
              </h3>
            </Col>
          )}
          <Col span={24}>
            {isSpecialtyEntity
              ? <SpecialtyList specialties={examsToDisplay} />
              : <ExamList exams={examsToDisplay} />
            }
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default ExamsStatus;